import {
  type ChangeEvent,
  Fragment,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { useLazyQuery, useQuery } from '@apollo/client/react';
import {
  Autocomplete,
  CircularProgress,
  type InputProps,
  ListItem,
  ListItemText,
  type SxProps,
  TextField,
  type Theme,
  debounce,
} from '@mui/material';
import { GlobeIcon } from 'lucide-react';

import { gql } from '@graphql/__generated__';
import type { ReadOnlyTenantSelectFragment } from '@graphql/__generated__/graphql';
import { SelectStartIcon } from '@modules/_shared/components/form/SelectStartIcon';
import { useLocale } from '@shared/hooks/locale';

export const READ_ONLY_TENANT_FRAGMENT = gql(/* GraphQL */ `
  fragment ReadOnlyTenantSelect on read_only_tenants {
    id
    name
  }
`);

const SEARCH_TENANTS = gql(/* GraphQL */ `
  query SearchReadOnlyTenants($where: read_only_tenants_bool_exp!) {
    read_only_tenants(where: $where, limit: 20) {
      ...ReadOnlyTenantSelect
    }
  }
`);

const GET_DEFAULT_TENANT = gql(/* GraphQL */ `
  query GetDefaultTenant($id: uuid!) {
    read_only_tenants(where: { id: { _eq: $id } }, limit: 1) {
      ...ReadOnlyTenantSelect
    }
  }
`);

type TenantSelectProps = {
  size?: 'small' | 'medium';
  /** Preselected tenant id (e.g. auto-matched). Displays the tenant on load. */
  tenantId?: string | null;
  onChange?: (tenantId: string | null) => void;
  InputProps?: InputProps;
  sx?: SxProps<Theme>;
  autoFocus?: boolean;
  blurOnSelect?: boolean;
  clearOnBlur?: boolean;
  excludeTenantId?: string[];
};

export const TenantSelect = ({
  tenantId,
  onChange,
  InputProps,
  autoFocus = false,
  sx,
  size = 'small',
  blurOnSelect = false,
  clearOnBlur = false,
  excludeTenantId,
}: TenantSelectProps) => {
  const { t } = useLocale();
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<ReadOnlyTenantSelectFragment[]>([]);
  const [selectedValue, setSelectedValue] =
    useState<ReadOnlyTenantSelectFragment | null>(null);

  const [searchTenants, { loading, error }] = useLazyQuery(SEARCH_TENANTS, {
    notifyOnNetworkStatusChange: true,
  });

  const { data: defaultTenant } = useQuery(GET_DEFAULT_TENANT, {
    skip: !tenantId,
    variables: { id: tenantId ?? '' },
  });

  useEffect(() => {
    if (tenantId == null) {
      setSelectedValue(null);
      return;
    }
    const tenant = defaultTenant?.read_only_tenants[0];
    if (tenant != null) {
      setSelectedValue(tenant);
    }
  }, [tenantId, defaultTenant]);

  const debouncedSearch = useMemo(
    () =>
      debounce(async value => {
        try {
          const response = await searchTenants({
            variables: {
              where: {
                name: { _ilike: `%${value}%` },
                id: { _nin: excludeTenantId },
              },
            },
          });
          setOpen(true);
          setOptions(response.data?.read_only_tenants ?? []);
        } catch (error) {
          console.error('Error searching tenants:', error);
        }
      }, 275),
    [searchTenants, excludeTenantId],
  );

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.value) {
      debouncedSearch(event.target.value);
    } else {
      setOpen(false);
      setOptions([]);
    }
  };

  return (
    <Autocomplete
      size={size}
      sx={sx}
      autoComplete={false}
      autoCorrect="off"
      open={open}
      onClose={() => {
        setOpen(false);
        setOptions([]);
      }}
      value={selectedValue}
      getOptionLabel={option => option.name || ''}
      options={options}
      loading={loading}
      clearOnBlur={clearOnBlur}
      blurOnSelect={blurOnSelect}
      loadingText={t('Loading...')}
      noOptionsText={error ? t('Error') : t('No results')}
      onChange={(_event, value) => {
        setSelectedValue(value);
        onChange?.(value?.id ?? null);
      }}
      onBlur={() => {
        if (clearOnBlur) {
          setSelectedValue(null);
        }
      }}
      filterOptions={x => x}
      renderOption={(props, option) => (
        <ListItem {...props} key={option.id}>
          <ListItemText primary={option.name} />
        </ListItem>
      )}
      renderInput={params => (
        <TextField
          {...params}
          autoFocus={autoFocus}
          onChange={handleSearch}
          variant="outlined"
          sx={{ background: 'var(--mui-palette-background-paper)' }}
          size="small"
          slotProps={{
            ...params.slotProps,

            input: {
              ...params.slotProps.input,
              placeholder: InputProps?.placeholder ?? t('Search tenant'),
              startAdornment: InputProps?.startAdornment ?? (
                <SelectStartIcon icon={GlobeIcon} />
              ),
              endAdornment: (
                <Fragment>
                  {loading ? (
                    <CircularProgress color="inherit" size={20} disableShrink />
                  ) : null}
                  {params.slotProps.input.endAdornment}
                </Fragment>
              ),
            },

            htmlInput: {
              ...params.slotProps.htmlInput,
              autoComplete: 'one-time-code', // disable autocomplete and autofill
              type: 'new-password',
              name: 'new-password',
            },
          }}
        />
      )}
      slotProps={{
        paper: {
          elevation: 3,
          sx: {
            borderRadius: '4px',
          },
        },
      }}
    />
  );
};
